import { AddOrEditLeadSourceModal } from "@/components/features/settings/defaults/lead-sources/AddOrEditLeadSourceModal";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveFirmLeadSourceOptions } from "@/lib/data/react-query/firm/firm-lead-source";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type FirmLeadSource } from "@/lib/types";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { customDateTimeSortingFns } from "@/lib/utils/tableUtils";

const LEAD_SOURCE_TABLE_COLUMN_IDS = [
  "Label",
  "Description",
  "Matter Status",
] as const;

const LEAD_SOURCES_TABLE_LS_KEY: string = tanstackTableNames.leadSources;

function LeadSourcesTableDangerous({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  const { hasActionPermission } = useUser();

  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.leadSource,
    ACTION_PERMISSION_VALUES.update,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [activeLeadSourceId, setActiveLeadSourceId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY);
    return stored.sorting ?? [];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY);
    const ids = [...LEAD_SOURCE_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY);
      return {
        ...stored.columnVisibility,
        Label: true,
      };
    },
  );

  const {
    data: leadSourcesData,
    isLoading: isLeadSourcesLoading,
    isError: isLeadSourcesError,
  } = useQuery(retrieveFirmLeadSourceOptions());

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const leadSources = useMemo(
    () => leadSourcesData?.data ?? [],
    [leadSourcesData?.data],
  );

  const matterStatusLabelById = useMemo(() => {
    const matterStatuses = matterSettingsData?.data.statuses ?? [];
    return new Map(matterStatuses.map((status) => [status.id, status.label]));
  }, [matterSettingsData?.data.statuses]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return leadSources;
    return leadSources.filter((row) => {
      const label = row.label.toLowerCase();
      const description = (row.description ?? "").toLowerCase();
      const statusLabel = row.statusId
        ? (
            row.status?.label ??
            matterStatusLabelById.get(row.statusId) ??
            ""
          ).toLowerCase()
        : "";
      return (
        label.includes(q) || description.includes(q) || statusLabel.includes(q)
      );
    });
  }, [leadSources, matterStatusLabelById, searchQuery]);

  const columns = useMemo<ColumnDef<FirmLeadSource>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (row) => row.label,
        header: "Label",
        enableHiding: false,
        cell: ({ row }) => (
          <Text variant="body" className="font-semibold">
            {row.original.label}
          </Text>
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Description",
        accessorFn: (row) => row.description ?? "",
        header: "Description",
        cell: ({ row }) => (
          <Text variant="subHeading">{row.original.description ?? "-"}</Text>
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Matter Status",
        accessorFn: (row) => {
          const statusId = row.statusId;
          return statusId
            ? (row.status?.label ?? matterStatusLabelById.get(statusId) ?? "")
            : "";
        },
        header: "Matter status",
        cell: ({ row }) => {
          const statusId = row.original.statusId;
          const label = statusId
            ? (row.original.status?.label ??
              matterStatusLabelById.get(statusId))
            : undefined;
          return <Text variant="subHeading">{label ?? "-"}</Text>;
        },
        sortingFn: "alphanumeric",
      },
    ],
    [matterStatusLabelById],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Label: true };
        persistTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: customDateTimeSortingFns,
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(LEAD_SOURCES_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  const activeLeadSource = leadSources.find(
    (source) => source.id === activeLeadSourceId,
  );

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={LEAD_SOURCE_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search lead sources...",
        }}
        isLoading={isLeadSourcesLoading}
        isError={isLeadSourcesError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No lead sources found."
        totalFilteredRowCount={filteredRows.length}
        entityNamePlural="lead sources"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.leadSources,
          "data-table-name": tanstackTableNames.leadSources,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            setActiveLeadSourceId(row.original.id);
            setIsEditing(true);
          },
        })}
      />

      <AddOrEditLeadSourceModal
        editing={false}
        open={isAdding}
        setOpen={setIsAdding}
      />
      {activeLeadSourceId && activeLeadSource ? (
        <AddOrEditLeadSourceModal
          activeLeadSource={activeLeadSource}
          isReadOnly={!hasUpdatePermission}
          editing
          open={isEditing}
          setOpen={setIsEditing}
          setActiveLeadSourceId={setActiveLeadSourceId}
        />
      ) : null}
    </div>
  );
}

export function LeadSourcesTable({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <LeadSourcesTableDangerous
        isAdding={isAdding}
        setIsAdding={setIsAdding}
      />
    </ErrorBoundary>
  );
}
